import {
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Menu as MenuIcon,
  UserRound,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import { menu, type GrupoMenu } from '@/app/navegacion';
import { useAuth } from '@/auth/AuthProvider';
import { puede, useMe } from '@/auth/queries';
import { cn, iniciales } from '@/lib/utils';

import { InsigniaEntorno } from './InsigniaEntorno';
import { SelectorTema } from './SelectorTema';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const CLAVE_COLAPSADO = 'cs-admin-menu-colapsado';
const CLAVE_GRUPOS_ABIERTOS = 'cs-admin-grupos-abiertos';

function leerColapsado(): boolean {
  try {
    return localStorage.getItem(CLAVE_COLAPSADO) === '1';
  } catch {
    return false;
  }
}

function leerGruposAbiertos(): Set<string> {
  try {
    const crudo = localStorage.getItem(CLAVE_GRUPOS_ABIERTOS);
    return new Set(crudo ? (JSON.parse(crudo) as string[]) : []);
  } catch {
    return new Set();
  }
}

/**
 * Ruta del menú que corresponde a la ubicación actual. Gana la ruta con el prefijo más largo y,
 * entre las del mismo path (p. ej. `/alumnos` y `/alumnos?status=blocked`), la que coincide en
 * más parámetros; una ruta con parámetros que no coinciden se descarta. Así solo hay un activo.
 */
function rutaActiva(grupos: GrupoMenu[], pathname: string, search: string): string | null {
  const actuales = new URLSearchParams(search);
  let mejor: { ruta: string; largo: number; params: number } | null = null;
  for (const item of grupos.flatMap((g) => g.items)) {
    if (item.pronto) continue;
    const [path = '/', query = ''] = item.ruta.split('?');
    const coincidePath = pathname === path || (path !== '/' && pathname.startsWith(`${path}/`));
    if (!coincidePath) continue;
    const params = [...new URLSearchParams(query)];
    if (params.some(([k, v]) => actuales.get(k) !== v)) continue;
    const candidato = { ruta: item.ruta, largo: path.length, params: params.length };
    if (
      !mejor ||
      candidato.largo > mejor.largo ||
      (candidato.largo === mejor.largo && candidato.params > mejor.params)
    ) {
      mejor = candidato;
    }
  }
  return mejor?.ruta ?? null;
}

const claseItem = (activo: boolean) =>
  cn(
    'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-white hover:bg-white/15',
    activo && 'bg-white/20 font-extrabold shadow-[inset_3px_0_0_var(--color-cian-500)]',
  );

function Grupo({
  grupo,
  colapsado,
  activa,
  abierto,
  onAlternar,
}: {
  grupo: GrupoMenu;
  colapsado: boolean;
  activa: string | null;
  abierto: boolean;
  onAlternar: () => void;
}) {
  const Icono = grupo.icono;
  const contieneActivo = grupo.items.some((i) => i.ruta === activa);
  if (colapsado) {
    const primero = grupo.items.find((i) => !i.pronto) ?? grupo.items[0];
    return (
      <Link
        to={primero?.ruta ?? '/'}
        title={grupo.etiqueta}
        aria-current={contieneActivo ? 'page' : undefined}
        className={cn(
          'grid h-10 w-10 place-items-center rounded-xl text-white hover:bg-white/15',
          contieneActivo && 'bg-white/20',
        )}
      >
        <Icono size={19} aria-hidden />
        <span className="sr-only">{grupo.etiqueta}</span>
      </Link>
    );
  }

  // Un grupo de un solo ítem con el mismo nombre (Inicio) no necesita encabezado plegable.
  const unico = grupo.items.length === 1 ? grupo.items[0] : undefined;
  if (unico && unico.etiqueta === grupo.etiqueta && !unico.pronto) {
    const activo = unico.ruta === activa;
    return (
      <Link
        to={unico.ruta}
        aria-current={activo ? 'page' : undefined}
        className={claseItem(activo)}
      >
        <Icono size={16} aria-hidden /> {unico.etiqueta}
      </Link>
    );
  }

  const idLista = `menu-${grupo.etiqueta.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <div>
      <button
        type="button"
        onClick={onAlternar}
        aria-expanded={abierto}
        aria-controls={idLista}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-[0.7rem] font-extrabold tracking-wider text-white/80 uppercase hover:bg-white/10 hover:text-white"
      >
        <Icono size={14} aria-hidden />
        <span className="flex-1 text-left">{grupo.etiqueta}</span>
        {!abierto && contieneActivo && (
          <span className="h-1.5 w-1.5 rounded-full bg-cian-500" aria-hidden />
        )}
        <ChevronDown
          size={14}
          aria-hidden
          className={cn('transition-transform duration-200', !abierto && '-rotate-90')}
        />
      </button>
      <div
        id={idLista}
        inert={!abierto}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          abierto ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <ul className="space-y-0.5 overflow-hidden pt-0.5">
          {grupo.items.map((item) => (
            <li key={item.ruta}>
              {item.pronto ? (
                <span
                  className="flex cursor-not-allowed items-center justify-between rounded-lg px-3 py-1.5 text-sm text-white/70"
                  title="Disponible en una fase siguiente"
                >
                  {item.etiqueta}
                  <span className="rounded-full bg-white/15 px-2 py-0.5 text-[0.65rem] font-bold">
                    Pronto
                  </span>
                </span>
              ) : (
                <Link
                  to={item.ruta}
                  aria-current={item.ruta === activa ? 'page' : undefined}
                  className={claseItem(item.ruta === activa)}
                >
                  {item.etiqueta}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MenuStaff() {
  const { data: me } = useMe();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-full py-1 pr-3 pl-1 outline-none hover:bg-[var(--pista)] data-[state=open]:bg-[var(--pista)]">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-azul-600 text-sm font-extrabold text-white">
          {iniciales(me?.name ?? '·')}
        </span>
        <span className="hidden max-w-44 truncate text-sm font-bold md:inline">{me?.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{me?.email}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => void navigate('/cuenta')}>
          <UserRound size={16} /> Mi cuenta
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void signOut().then(() => navigate('/login', { replace: true }))}
        >
          <LogOut size={16} /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Shell del panel: menú lateral por secciones (colapsable) y barra superior. Prioridad escritorio. */
export function AppShell() {
  const { data: me } = useMe();
  const [colapsado, setColapsado] = useState(leerColapsado);
  const [abiertoMovil, setAbiertoMovil] = useState(false);

  const alternar = () =>
    setColapsado((c) => {
      try {
        localStorage.setItem(CLAVE_COLAPSADO, c ? '0' : '1');
      } catch {
        /* sin almacenamiento */
      }
      return !c;
    });

  const grupos = menu.filter((g) => !g.seccion || puede(me, g.seccion, 'ver'));
  const { pathname, search } = useLocation();
  const activa = rutaActiva(grupos, pathname, search);
  // Los grupos empiezan colapsados; se recuerdan solo los que el staff abre a mano.
  const [abiertos, setAbiertos] = useState(leerGruposAbiertos);

  const alternarGrupo = (etiqueta: string) =>
    setAbiertos((prev) => {
      const siguiente = new Set(prev);
      if (!siguiente.delete(etiqueta)) siguiente.add(etiqueta);
      try {
        localStorage.setItem(CLAVE_GRUPOS_ABIERTOS, JSON.stringify([...siguiente]));
      } catch {
        /* sin almacenamiento */
      }
      return siguiente;
    });

  // El grupo de la página actual se abre (también al entrar) para mostrar dónde estás.
  const grupoActivo = grupos.find((g) => g.items.some((i) => i.ruta === activa))?.etiqueta;
  const [grupoVisto, setGrupoVisto] = useState<string>();
  if (grupoActivo !== grupoVisto) {
    setGrupoVisto(grupoActivo);
    if (grupoActivo && !abiertos.has(grupoActivo)) {
      setAbiertos(new Set(abiertos).add(grupoActivo));
    }
  }

  const lateral = (enMovil: boolean) => (
    <nav
      aria-label="Secciones del panel"
      className={cn(
        'bg-lateral flex h-full flex-col gap-5 overflow-y-auto py-4 text-white',
        colapsado && !enMovil ? 'w-16 items-center px-3' : 'w-64 px-3',
      )}
    >
      <Link
        to="/"
        className={cn(
          'flex items-center gap-2 text-white',
          colapsado && !enMovil ? 'justify-center' : 'px-2',
        )}
        aria-label="Inicio del panel"
      >
        <img
          src="/img/isotipo-cerebro-blanco.png"
          alt=""
          className="h-8 w-8 shrink-0 object-contain"
        />
        {(!colapsado || enMovil) && (
          <span className="leading-tight font-extrabold">
            Consalud
            <span className="block text-xs font-bold text-white/85">Panel administrativo</span>
          </span>
        )}
      </Link>
      <div className="flex-1 space-y-2">
        {grupos.map((g) => (
          <Grupo
            key={g.etiqueta}
            grupo={g}
            colapsado={colapsado && !enMovil}
            activa={activa}
            abierto={abiertos.has(g.etiqueta)}
            onAlternar={() => alternarGrupo(g.etiqueta)}
          />
        ))}
      </div>
      {!enMovil && (
        <button
          type="button"
          onClick={alternar}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/85 hover:bg-white/15"
          aria-label={colapsado ? 'Expandir menú' : 'Colapsar menú'}
        >
          {colapsado ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          {!colapsado && 'Colapsar'}
        </button>
      )}
    </nav>
  );

  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 hidden h-dvh shrink-0 lg:block">{lateral(false)}</aside>
      {abiertoMovil && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="overlay-cs absolute inset-0"
            aria-label="Cerrar menú"
            onClick={() => setAbiertoMovil(false)}
          />
          {/* Al elegir un enlace, el menú móvil se cierra. */}
          <aside
            className="relative h-full w-64"
            onClickCapture={(e) => {
              if ((e.target as HTMLElement).closest('a')) setAbiertoMovil(false);
            }}
          >
            {lateral(true)}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[var(--separador)] bg-[var(--fondo-base)]/85 px-4 py-2 backdrop-blur">
          <button
            type="button"
            className="rounded-lg p-2 hover:bg-[var(--pista)] lg:hidden"
            onClick={() => setAbiertoMovil(true)}
            aria-label="Abrir menú"
          >
            {abiertoMovil ? <X size={20} /> : <MenuIcon size={20} />}
          </button>
          <div className="flex-1" />
          <SelectorTema />
          <MenuStaff />
        </header>
        <main className="w-full flex-1 px-4 py-6 lg:px-8">
          <Outlet />
        </main>
      </div>
      <InsigniaEntorno />
    </div>
  );
}
